import React, { useEffect, useState, useRef } from 'react';
import { useNoteSelection } from '../context/NoteSelectionContext';
import TopNavbar from '../components copy/TopNavbar';
import RightSidebar from '../components copy/RightSidebar';
import Notification from '../components copy/Notification';
import ConfirmationModal from '../components copy/ConfirmationModal';
import FeedbackModal from '../components copy/FeedbackModal';
import configDetails from '../Config/Config';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import routesfe from '../Config/RoutesFE';
import { BsMicFill, BsPlayFill, BsPauseFill, BsVolumeUpFill, BsExclamationTriangleFill, BsTrash, BsCheckCircleFill, BsXCircleFill, BsClockFill, BsArrowLeft, BsPersonFill, BsShareFill  } from 'react-icons/bs';
import ShareModal from '../components copy/ShareModal';
import { useRecord } from '../components copy/RecordContext';
import '../Styles/MainHome.css';
import DOMPurify from 'dompurify';
import ReplayTab from '../components copy/Replay';
import { batchConvertS3UrisToPresignedUrls } from '../utils/imageUtils';
import logger from '../utils/Logger';

const COMPONENT_NAME = 'MainHome';

// Helper to format seconds with smart formatting based on duration
function formatNoteTime(seconds) {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '0:00';
  
  const totalSeconds = Math.floor(seconds);
  
  // Show 0:00 when seconds is 0
  if (totalSeconds === 0) {
    return '0:00';
  }
  
  // Less than 1 minute - show seconds
  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }
  
  // Less than 1 hour - show MM:SS
  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  // 1 hour or more - show HH:MM:SS
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
// Helper to check if there's an active recording session
function getActiveRecordingSession() {
  const isActive = getCookie('conversation_active') === 'true';
  const conversationId = getCookie('conversation_id');
  const status = getCookie('recording_status');
  
  if (isActive && conversationId && status === '🎙️ Listening...') {
    return conversationId;
  }
  return null;
}
// Helper to clear all session cookies when session is detected as stopped
function clearSessionCookies() {
  const sessionCookies = [
    'recording_start_time',
    'recording_status',
    'is_recording_paused',
    'pause_start_time',
    'total_pause_duration',
    'last_activity_time',
    'original_session_id',
    'conversation_id',
    'conversation_active',
    'session_id',
    'transcription_token',
    'token_issued_at',
    'token_expires_at',
    'token_status',
    'isLiveSession',
    'liveSessionId'
  ];
  sessionCookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
  localStorage.removeItem('session_id');
  localStorage.removeItem('transcript_segments');
}

// Helper to validate if a session is still live via API
function checkSessionIsLive(conversationId, notes) {
  if (!conversationId || !notes || notes.length === 0) {
    return { isLive: false, status: null };
  }
  
  // Find the note with matching conversation_id
  const matchingNote = notes.find(r => r.conversation_id === conversationId);
  
  if (!matchingNote) {
    return { isLive: false, status: null };
  }
  
  // Check live_status.current_status if available
  if (matchingNote.live_status && matchingNote.live_status.current_status) {
    const currentStatus = matchingNote.live_status.current_status;
    const isLive = currentStatus === 'LIVE' || currentStatus === 'PAUSED';
    return { isLive, status: currentStatus };
  }
  
  // Fallback to note status
  const noteStatus = matchingNote.status;
  const isLive = noteStatus === 'created' || noteStatus === 'inprogress';
  
  // If status is 'processing' or 'completed', the session was stopped
  if (noteStatus === 'processing' || noteStatus === 'completed') {
    return { isLive: false, status: noteStatus };
  }
  
  return { isLive, status: noteStatus };
}
const { baseUrl, META_DATA, DELETE_NOTE, TRANSCRIPT, SUMMARY_TEXT, POLLING_INTERVAL, PUBLIC_ACCESS } = configDetails;
// Cookie helper function
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Get all user details from localStorage
function getAuthenticatedUserDetails() {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {}
  }
  return null;
}

// Get user id (cognito_user_id or fallback)
function getAuthenticatedUserId() {
  const user = getAuthenticatedUserDetails();
  // Always prefer userSub for user_id fetches (backend expects this)
  if (user && user.userSub) return user.userSub;
  return null;
}
function getUserName() {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      return user.name || user.email || 'User';
    } catch {}
  }
  return 'User';
}

// Function to process transcript segments and convert S3 URIs to image HTML
const processTranscriptSegments = async (segments, authIdToken) => {
  if (!segments || !Array.isArray(segments)) return segments;
  
  // Separate image segments from text segments
  const imageSegments = segments.filter(segment => segment.content_type === 'image');
  const textSegments = segments.filter(segment => segment.content_type !== 'image');
  
  // Batch convert all image S3 URIs to presigned URLs
  const presignedUrlMap = await batchConvertS3UrisToPresignedUrls(imageSegments, authIdToken);
  
  // Process all segments
  const processedSegments = segments.map((segment, index) => {
    if (!segment?.full_message) return segment;
    
    let processedMessage = segment.full_message;
    
    // Handle image content type
    if (segment.content_type === 'image') {
      const presignedUrl = presignedUrlMap[segment.div_id];
      if (presignedUrl) {
        processedMessage = `<img src="${presignedUrl}" alt="Uploaded image" style="max-width: 300px; height: auto; border-radius: 8px; margin: 10px 0;" />`;
      } else {
        // Fallback error display
        processedMessage = `<div style="color: #dc3545; font-style: italic; padding: 10px; border: 1px solid #dc3545; border-radius: 8px; margin: 10px 0;">❌ Failed to load image</div>`;
      }
    }
    
    return {
      ...segment,
      full_message: processedMessage
    };
  });
  
  return processedSegments;
};

// Audio Player Component
const AudioPlayer = ({ audioUrl, audioPath, s3_path, timeInSeconds, onPlayStateChange, onTimeUpdate, onSeekRequest }) => {
  // Restart helper
  const restartAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };
  // Seek helpers
  const seekBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 5);
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const seekForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  // Bar and circle sizes for inline styles
  const barHeight = 8;
  const barWidth = '100%';
  const circleSize = 16;

  // Helper to format seconds for right side of progress bar - always show MM:SS format
  const formatNoteTimeRight = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) return '0:00';
    
    const totalSeconds = Math.floor(seconds);
    
    // Show 0:00 when seconds is 0
    if (totalSeconds === 0) {
      return '0:00';
    }
    
    // For audio player duration display, always show MM:SS format
    if (totalSeconds < 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    
    // 1 hour or more - show HH:MM:SS
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // State for player
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(timeInSeconds || 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);

  // Always use timeInSeconds for the right display, fallback to 0 if invalid
  const rightDisplaySeconds = (typeof timeInSeconds === 'number' && !isNaN(timeInSeconds) && timeInSeconds >= 0)
    ? timeInSeconds
    : 0;

  // References
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const animationRef = useRef(null);

  // Play/pause toggle
  const togglePlayPause = () => {
  if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (onPlayStateChange) onPlayStateChange(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      if (onPlayStateChange) onPlayStateChange(true);
    }
  };

  // Handle progress bar click (seek)
  const handleProgressClick = (e) => {
    if (!progressBarRef.current || !audioRef.current || !duration) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const pos = clickX / width;
    const clampedPos = Math.max(0, Math.min(1, pos));
    const newTime = clampedPos * duration;
    if (isFinite(newTime) && !isNaN(newTime) && newTime >= 0 && newTime <= duration) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Handle metadata loaded
  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;

    try {
      // If timeInSeconds is provided, use that as the duration
      if (timeInSeconds && isFinite(timeInSeconds) && !isNaN(timeInSeconds) && timeInSeconds > 0) {
        setDuration(timeInSeconds);
      } else {
        // Otherwise fall back to the audio element's duration
        const audioDuration = audioRef.current.duration;

        // Validate that duration is a proper number
        if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
          setDuration(audioDuration);
        } else {
          // Keep the current duration
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Handle audio end
  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    cancelAnimationFrame(animationRef.current);
    if (onPlayStateChange) onPlayStateChange(false);
    if (onTimeUpdate) onTimeUpdate(0);
  };

  // Handle errors
  const handleLoadError = (e) => {

    // Get detailed error information
    let errorMsg = 'Error loading audio.';

    // Add specific details about what we tried
    if (audioUrl) {
      errorMsg += ` Primary URL failed.`;
    }

    if (audioPath) {
      errorMsg += ` Path fallback failed.`;
    }

    if (s3_path) {
      errorMsg += ` S3 path fallback failed.`;
    }

    setLoading(false);
    setError(`${errorMsg} Please check console for details.`);
  };

  // Change playback speed
  const changePlaybackRate = (rate) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  // Format time display for audio player - always show MM:SS format
  const formatTime = (timeInSeconds) => {
    if (timeInSeconds === undefined || timeInSeconds === null || isNaN(timeInSeconds) || !isFinite(timeInSeconds) || timeInSeconds < 0) {
      return "0:00";
    }
    try {
      const totalSeconds = Math.floor(timeInSeconds);
      
      // Show 0:00 when seconds is 0
      if (totalSeconds === 0) {
        return "0:00";
      }
      
      // For audio player, always show MM:SS format
      if (totalSeconds < 3600) {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      
      // 1 hour or more - show HH:MM:SS
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } catch (error) {
      return "0:00";
    }
  };

  // Reset player when URL changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoading(true);
    setError(null);

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    if (!audioUrl || typeof audioUrl !== 'string') {
      setError('Invalid audio source.');
      setLoading(false);
    }
  }, [audioUrl]);

  // ── Expose seek+play to parent via onSeekRequest
  useEffect(() => {
    if (!onSeekRequest) return;
    onSeekRequest((seconds) => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = seconds;
      setCurrentTime(seconds);
      if (onTimeUpdate) onTimeUpdate(seconds);
      audio.play().then(() => {
        setIsPlaying(true);
        if (onPlayStateChange) onPlayStateChange(true);
      }).catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSeekRequest]);

    // Add event listener to audio element for timeupdate
    useEffect(() => {
      const audio = audioRef.current;
      // Create a function to handle time updates
      const handleTimeUpdate = () => {
        if (audio && audio.readyState > 0) {
          let currentTime = audio.currentTime || 0;
          if (!isFinite(currentTime) || isNaN(currentTime)) currentTime = 0;
          setCurrentTime(currentTime);
          if (onTimeUpdate) onTimeUpdate(currentTime);
        }
      };
      // Add the event listener
      if (audio) {
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleTimeUpdate);
      }
      // Cleanup on unmount
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (audio) {
          audio.removeEventListener('timeupdate', handleTimeUpdate);
          audio.removeEventListener('loadedmetadata', handleTimeUpdate);
        }
      };
    }, []);

  return (
    <div className="audio-player mb-2">
      {/* Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleAudioEnd}
        onError={(e) => {

          // If we have audioPath, try a different URL construction as fallback
          if (audioPath) {
            const fallbackUrl = `${baseUrl}/get_audio?path=${encodeURIComponent(audioPath)}`;

            // Only update if it's different from the current URL
            if (fallbackUrl !== audioUrl) {
              e.currentTarget.src = fallbackUrl;
              e.currentTarget.load();
              return; // Let the audio element try the new source
            }
          }

          // If we have s3_path as a fallback
          if (s3_path) {
            const fallbackUrl = `${baseUrl}/get_audio?s3_path=${encodeURIComponent(s3_path)}`;

            // Only update if it's different from the current URL
            if (fallbackUrl !== audioUrl) {
              e.currentTarget.src = fallbackUrl;
              e.currentTarget.load();
              return; // Let the audio element try the new source
            }
          }

          // If all fallbacks failed, call the error handler
          handleLoadError(e);
        }}
      />

      {/* Error State */}
      {error ? (
        <div className="modern-audio-player mainhome-audio-loading" >
          {/* Animated Waves Loading Indicator */}
          <div className="mainhome-waves d-flex align-items-center justify-content-center mb-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`mainhome-wave mainhome-wave-${i}`}
                style={{ height: `${15 + Math.sin(i/2) * 10}px` }}
              />
            ))}
          </div>
          {/* Loading Text */}
          <div className="d-flex align-items-center">
            <span className="mainhome-audio-loading-text text-primary fw-medium me-2">Loading audio</span>
            <div className="mainhome-loading-dots loading-dots">
              <span className="mainhome-dot mainhome-dot-0">.</span>
              <span className="mainhome-dot mainhome-dot-1">.</span>
              <span className="mainhome-dot mainhome-dot-2">.</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="modern-audio-player mainhome-audio-ui">
          {/* Time Display Row - aligned with bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: barWidth,
              margin: '0 auto 4px auto',
              fontSize: 14,
              color: '#495057',
            }}
          >
            <div className="time-display fw-medium mainhome-audio-time">{formatTime(currentTime)}</div>
            {/* Show formatted total seconds on right of progress bar, fallback to duration if timeInSeconds is not valid */}
            <div className="duration text-muted mainhome-audio-duration" style={{ minWidth: 60, textAlign: 'right' }}>
              {formatNoteTimeRight(rightDisplaySeconds)}
            </div>
          </div>
          {/* Progress Bar with enhanced design - reduced length */}
          <div
            ref={progressBarRef}
            onClick={handleProgressClick}
            className="mainhome-progress-bar"
            style={{
              position: 'relative',
              width: barWidth,
              height: barHeight,
              background: '#e9ecef',
              borderRadius: 10,
              margin: '24px auto',
              cursor: 'pointer',
              overflow: 'visible',
              display: 'block',
            }}
          >
            {/* Track Fill with gradient */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: barHeight,
                width: `${!duration || duration <= 0 ? 0 : (currentTime/duration * 100)}%`,
                background: 'linear-gradient(90deg, #1a73e8, #3b82f6)',
                borderRadius: 10,
                transition: 'width 0.05s linear',
                zIndex: 1,
              }}
            ></div>
            {/* Circle Indicator - hidden by default, visible on hover */}
            <div
              className="mainhome-progress-circle"
              style={{
                left: `${!duration || duration <= 0 ? 0 : (currentTime/duration * 100)}%`,
                width: circleSize,
                height: circleSize,
              }}
            ></div>
          </div>

          {/* Controls Row with enhanced design - centered */}
          <div className="d-flex align-items-center justify-content-center">
            {/* 5s Backward Button - outlined style */}
            <button
              className="mainhome-btn-seek-filled me-2"
              onClick={seekBackward}
              title="Back 5 seconds"
            >
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 9L13 14L18 19" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {/* Restart Button */}
            <button
              className="btn p-0 me-2 d-flex align-items-center justify-content-center mainhome-btn-restart"
              onClick={restartAudio}
              title="Restart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="#3b82f6" viewBox="0 0 16 16">
                <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
              </svg>
            </button>
            {/* Play Button */}
            <button
              className="btn p-0 me-2 d-flex align-items-center justify-content-center mainhome-btn-play"
              onClick={togglePlayPause}
            >
              {isPlaying ?
                <BsPauseFill size={28} className="mainhome-btn-icon" /> :
                <BsPlayFill size={30} className="mainhome-btn-icon mainhome-btn-icon-play" />
              }
            </button>
            {/* 10s Forward Button - outlined style */}
            <button
              className="mainhome-btn-seek-filled me-2"
              onClick={seekForward}
              title="Forward 10 seconds"
            >
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 9L15 14L10 19" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {/* Enhanced Playback Rate Dropdown */}
            <div className="dropdown">
              <button
                className="btn btn-sm rounded-pill text-primary fw-medium mainhome-btn-speed"
                type="button"
                id="speedDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {playbackRate}x
              </button>
              <ul className="dropdown-menu shadow-sm border-light" aria-labelledby="speedDropdown">
                {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map((rate) => (
                  <li key={rate}>
                    <button
                      className={`dropdown-item${playbackRate === rate ? ' active bg-primary text-white' : ''}`}
                      onClick={() => changePlaybackRate(rate)}
                    >
                      {rate}x
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ flex: 1 }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Strip HTML tags to get plain text for editing
function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Word-level diff using LCS: returns array of { text, changed } objects
function wordDiff(original, edited) {
  if (!original && !edited) return [];
  if (original === edited) return [{ text: original, changed: false }];
  const origWords = original.split(/(\s+)/);
  const editWords = edited.split(/(\s+)/);
  // Build LCS table
  const m = origWords.length;
  const n = editWords.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (origWords[i - 1] === editWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  // Backtrack to find diff
  const result = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && origWords[i - 1] === editWords[j - 1]) {
      result.unshift({ text: editWords[j - 1], changed: false });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ text: editWords[j - 1], changed: true });
      j--;
    } else {
      i--;
    }
  }
  return result;
}

const MainHome = ({ authIdToken }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingNotes, setDeletingNotes] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, noteToDelete: null });
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false });
  const [publicAccess, setPublicAccess] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const { selectedNote, selectNote, deselectNote, homeReset, triggerHomeReset } = useNoteSelection();
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [firstPage, setFirstPage] = useState(1);
  const NOTES_PER_PAGE = 10; // Backend returns 10 notes per page
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams();
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const audioSeekFnRef = useRef(null);
 
  // RightSidebar visibility state - persist across refresh via localStorage
  const [rightSidebarVisible, setRightSidebarVisible] = useState(() => {
    const saved = localStorage.getItem('rightSidebarVisible');
    if (saved !== null) return saved === 'true';
    return window.innerWidth >= 768;
  });
  
  // Polling ref for summary updates
  const pollingRef = useRef(null);

  // ADDED: refs for transcript auto-scroll
  const transcriptScrollRef = useRef(null);
  const lastSegmentRef = useRef(null);

  // Persist right sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('rightSidebarVisible', String(rightSidebarVisible));
  }, [rightSidebarVisible]);

  // Handle window resize - hide on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setRightSidebarVisible(false); // Hidden on mobile
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for conversation deleted state and show notification
  useEffect(() => {
    if (location.state?.conversationDeleted) {
      const message = location.state?.deletedMessage || 'This conversation no longer exists';
      
      // Check if user is authenticated
      const userData = localStorage.getItem('user');
      const isAuthenticated = userData !== null;
      
      if (!isAuthenticated) {
        // Unauthorized user - navigate to landing page
        navigate(routesfe.LANDING);
        return;
      }
      
      // Authenticated user - show notification
      setNotification({ message, type: 'error', duration: 5000 });
      
      // Clear the state by replacing history without the state
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);
  
  // Reset audio playback state when selected note changes
  useEffect(() => {
    setAudioIsPlaying(false);
    setAudioCurrentTime(0);
  }, [selectedNote?.conversation_id]);

  // ADDED: auto-scroll transcript to the latest segment when new segments arrive
  useEffect(() => {
    const container = transcriptScrollRef.current;
    if (!container) return;
    const threshold = 120; // px from bottom counted as "near bottom"
    const nearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    if (nearBottom) {
      // Defer to next frame so newly rendered segment is measured
      requestAnimationFrame(() => {
        if (lastSegmentRef.current) {
          lastSegmentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [selectedNote?.transcriptionSegments?.length, selectedNote?.conversation_id]);

  // ADDED: keep the active/last segment in view as playback advances
  useEffect(() => {
    if (!audioIsPlaying) return;
    if (lastSegmentRef.current) {
      lastSegmentRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [audioCurrentTime, audioIsPlaying]);


// GET public access status when a note page mounts (refresh after Stop).
  // The Share popup re-fetches on every open, so this is only a pre-warm.
  useEffect(() => {
    if (!conversationId) { setPublicAccess(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${baseUrl}${PUBLIC_ACCESS}/${conversationId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken }
        });
        if (!res.ok) throw new Error(`Failed to fetch public access: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setPublicAccess(data);
      } catch (error) {
        if (!cancelled) setPublicAccess(null);
        logger.error('Failed to fetch public access status', {
          component: COMPONENT_NAME,
          action: 'FETCH_PUBLIC_ACCESS_ERROR',
          error,
          context: { conversationId }
        });
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, authIdToken]);

  // Toggle sidebar visibility
  const toggleRightSidebar = () => {
    setRightSidebarVisible(!rightSidebarVisible);
  };

  // Show notification function
  const showNotification = (message, type = 'info', duration = 4000) => {
    setNotification({ message, type, duration });
  };

  // Close notification function
  const closeNotification = () => {
    setNotification(null);
  };

  const fetchConversationId = async (sessionId) => {
  const user = getAuthenticatedUserDetails();
  const userId = user && user.userSub;
  if (!userId || !sessionId) {
    console.error('Missing userId or sessionId');
    return null;
  }

  try {
    const response = await fetch(
      `${baseUrl}${META_DATA}?user_id=${userId}&session_id=${sessionId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch meta data: ${response.status}`);
    }

    const data = await response.json();    
    // Extract conversation_id from the response
    if (data.records && Array.isArray(data.records) && data.records.length > 0) {
      const record = data.records.find(r => r.session_id === sessionId);
      return record?.conversation_id || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching conversation_id from meta_data:', error);
    return null;
  }
};

  // Function to fetch transcript data
  const fetchTranscriptData = async (sessionId, providedConversationId = null, silent = false) => {
    const user = getAuthenticatedUserDetails();
    const userId = user && user.userSub;
    if (!userId || !sessionId) {
      logger.error('Fetch transcript failed - missing credentials', {
        component: COMPONENT_NAME,
        action: 'FETCH_TRANSCRIPT_ERROR',
        context: { hasUserId: !!userId, hasSessionId: !!sessionId }
      });
      if (!silent) showNotification('Error: Missing user ID or session ID', 'error');
      return null;
    }

    logger.info('Fetching transcript data', {
      component: COMPONENT_NAME,
      action: 'FETCH_TRANSCRIPT_START',
      userId,
      context: {
        sessionId,
        providedConversationId: providedConversationId || null,
        timestamp: Date.now()
      }
    });

    try {
      let conversationId = providedConversationId;
      if (!conversationId && selectedNote && selectedNote.conversation_id) {
        conversationId = selectedNote.conversation_id;
      } else if (!conversationId && notes && notes.length > 0) {
        // Try to find note with matching sessionId
        const noteMatch = notes.find(note => note.session_id === sessionId);
        if (noteMatch && noteMatch.conversation_id) {
          conversationId = noteMatch.conversation_id;
        }
      }
       //  Do NOT call META_DATA on refresh
      if (!conversationId) {
        logger.warn('Conversation ID missing for transcript fetch', {
          component: COMPONENT_NAME,
          action: 'FETCH_TRANSCRIPT_SKIP',
          userId,
          context: { sessionId }
        });
        return null;
      }
      const response = await fetch(
        `${baseUrl}${TRANSCRIPT}?user_id=${userId}&session_id=${sessionId}&conversation_id=${conversationId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch transcript: ${response.status}`);
      }

      const data = await response.json();
      
      // Keep transcription as segments array - DO NOT flatten
      let transcriptionSegments = [];
      if (data.transcription && Array.isArray(data.transcription)) {
        transcriptionSegments = data.transcription.map((item, index) => ({
          div_id: item.div_id || `segment-${index}`,
          full_message: item.full_message || '',
          content_type: item.content_type || 'text',
          start_time: item.start_time != null ? item.start_time : null,
          end_time: item.end_time != null ? item.end_time : null,
          expected_output: item.expected_output || null,
          annotated_at: item.annotated_at || null,
          reprocess_enabled: item.reprocess_enabled || false
        }));
      }

      // Process transcript segments to convert S3 URIs to images
      const processedSegments = await processTranscriptSegments(transcriptionSegments, authIdToken);

      logger.info('Transcript data fetched successfully', {
        component: COMPONENT_NAME,
        action: 'FETCH_TRANSCRIPT_SUCCESS',
        userId,
        context: {
          sessionId,
          conversationId,
          segmentsCount: processedSegments?.length || 0,
          hasAudio: Boolean(data.audio_url),
          secondsUsed: data.seconds_used,
          timestamp: Date.now()
        }
      });

      return {
        transcriptionSegments: processedSegments,
        audioUrl: data.audio_url,
        seconds_used: data.seconds_used,
        hasAudio: Boolean(data.audio_url),
        conversation_id: conversationId
      };
    } catch (error) {
      logger.error('Failed to fetch transcript', {
        component: COMPONENT_NAME,
        action: 'FETCH_TRANSCRIPT_ERROR',
        userId,
        error,
        context: { sessionId, timestamp: Date.now() }
      });
      if (!silent) showNotification('Failed to load transcript data', 'error');
      return null;
    }
  };

  // Function to fetch summary data
  const fetchSummaryData = async (sessionId) => {
    const user = getAuthenticatedUserDetails();
    const userId = user && user.userSub;
    if (!userId || !sessionId) {
      logger.error('Fetch summary failed - missing credentials', {
        component: COMPONENT_NAME,
        action: 'FETCH_SUMMARY_ERROR',
        context: { hasUserId: !!userId, hasSessionId: !!sessionId }
      });
      showNotification('Error: Missing user ID or session ID', 'error');
      return null;
    }

    logger.info('Fetching summary data', {
      component: COMPONENT_NAME,
      action: 'FETCH_SUMMARY_START',
      userId,
      context: { sessionId, timestamp: Date.now() }
    });

    try {
      const response = await fetch(
        `${baseUrl}${SUMMARY_TEXT}?session_id=${sessionId}&user_id=${userId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch summary: ${response.status}`);
      }

      const data = await response.json();
      
      logger.info('Summary data fetched successfully', {
        component: COMPONENT_NAME,
        action: 'FETCH_SUMMARY_SUCCESS',
        userId,
        context: {
          sessionId,
          hasSummary: !!(data.summary_text || data.summary),
          timestamp: Date.now()
        }
      });
      
      return {
        summary: data.summary_text || data.summary || 'No summary available'
      };
    } catch (error) {
      logger.error('Failed to fetch summary', {
        component: COMPONENT_NAME,
        action: 'FETCH_SUMMARY_ERROR',
        userId,
        error,
        context: { sessionId, timestamp: Date.now() }
      });
      showNotification('Failed to load summary data', 'error');
      return null;
    }
  };

  // Handler for summary tab click
  const handleSummaryTabClick = async () => {
    // Exit annotation mode if active
    if (isAnnotationMode) {
      cleanupAnnotationAudio();
      setIsAnnotationMode(false);
      setAnnotationSegments([]);
      setAnnotationSaveStatus({});
    }
    const userId = getAuthenticatedUserId();
    
    logger.info('Summary tab clicked', {
      component: COMPONENT_NAME,
      action: 'CLICK_SUMMARY_TAB',
      userId,
      context: {
        sessionId: selectedNote?.session_id || null,
        conversationId: selectedNote?.conversation_id || null,
        noteStatus: selectedNote?.status || null,
        timestamp: Date.now()
      }
    });
    
    localStorage.setItem(`note_view_${selectedNote.session_id}`, 'summary');
    if (!selectedNote || !selectedNote.session_id) return;

    // Get conversation_id and navigate to new URL format
    if (selectedNote.conversation_id) {
      navigate(`/conversation/${selectedNote.conversation_id}/summary`, { replace: true });
      return;
    }

    // Find the original note to preserve timestamp and other metadata
    const originalNote = notes.find(note => note.session_id === selectedNote.session_id);
    const noteStatus = originalNote?.status || selectedNote.status;
    
    // Only redirect to record page if the note is truly live (created/inprogress status)
    // Do NOT redirect for 'processing' or 'completed' notes (session was stopped)
    if (isLiveSession && liveSessionId && selectedNote.session_id === liveSessionId) {
      if (noteStatus === 'created' || noteStatus === 'inprogress') {
        navigate(`/record/${liveSessionId}`);
        return;
      }
    }

    // Always try to fetch summary from API when tab is clicked, regardless of status
    selectNote({
      ...selectedNote,
      timestamp: originalNote?.timestamp || selectedNote.timestamp || Date.now(),
      time_in_seconds: originalNote?.time_in_seconds || selectedNote.time_in_seconds,
      user_name: originalNote?.user_name || selectedNote.user_name || getUserName(),
      view: 'summary',
      status: originalNote?.status || selectedNote.status || 'completed', // preserve original status
      loadingSummary: true
    });  
    
    // Fetch summary data
    const summaryData = await fetchSummaryData(selectedNote.session_id);
    if (summaryData) {
      selectNote({
        ...selectedNote,
        timestamp: originalNote?.timestamp || selectedNote.timestamp || Date.now(),
        time_in_seconds: originalNote?.time_in_seconds || selectedNote.time_in_seconds,
        user_name: originalNote?.user_name || selectedNote.user_name || getUserName(),
        view: 'summary',
        status: originalNote?.status || selectedNote.status || 'completed', // preserve original status
        loadingSummary: false,
        summary: summaryData.summary
      });
    }
  };
  // Handler for transcript tab click
  const handleTranscriptTabClick = async () => {
    if (isAnnotationMode) {
      cleanupAnnotationAudio();
      setIsAnnotationMode(false);
      setAnnotationSegments([]);
      setAnnotationSaveStatus({});
    }
    const userId = getAuthenticatedUserId();
    
    logger.info('Transcript tab clicked', {
      component: COMPONENT_NAME,
      action: 'CLICK_TRANSCRIPT_TAB',
      userId,
      context: {
        sessionId: selectedNote?.session_id || null,
        conversationId: selectedNote?.conversation_id || null,
        noteStatus: selectedNote?.status || null,
        timestamp: Date.now()
      }
    });
    
    localStorage.setItem(`note_view_${selectedNote.session_id}`, 'transcript');
    if (!selectedNote || !selectedNote.session_id) return;
    
    // Get conversation_id and navigate to new URL format
    if (selectedNote.conversation_id) {
      navigate(`/conversation/${selectedNote.conversation_id}/transcript`, { replace: true });
      return;
    }
    
    // Find the original note to preserve timestamp and other metadata
    const originalNote = notes.find(note => note.session_id === selectedNote.session_id);
    const noteStatus = originalNote?.status || selectedNote.status;
    
    // Only redirect to record page if the note is truly live (created/inprogress status)
    // Do NOT redirect for 'processing' or 'completed' notes (session was stopped)
    if (isLiveSession && liveSessionId && selectedNote.session_id === liveSessionId) {
      if (noteStatus === 'created' || noteStatus === 'inprogress') {
        navigate(`/record/${liveSessionId}`);
        return;
      }
      // If status is processing or completed, clear live session cookies
      if (noteStatus === 'processing' || noteStatus === 'completed') {
        clearSessionCookies();
      }
    }
    
    // Update view immediately and show loading, preserving all existing data
    selectNote({ 
      ...selectedNote,
      timestamp: originalNote?.timestamp || selectedNote.timestamp || Date.now(),
      time_in_seconds: originalNote?.time_in_seconds || selectedNote.time_in_seconds,
      user_name: originalNote?.user_name || selectedNote.user_name || getUserName(),
      view: 'transcript',
      transcriptionSegments: null,
      loadingTranscript: true
    });

    // Fetch transcript data
    const transcriptData = await fetchTranscriptData(selectedNote.session_id, selectedNote.conversation_id);
    
    if (transcriptData) {
      selectNote({ 
        ...selectedNote,
        timestamp: originalNote?.timestamp || selectedNote.timestamp || Date.now(),
        time_in_seconds: transcriptData.seconds_used || originalNote?.time_in_seconds || selectedNote.time_in_seconds,
        user_name: originalNote?.user_name || selectedNote.user_name || getUserName(),
        view: 'transcript',
        transcriptionSegments: transcriptData.transcriptionSegments,
        audioUrl: transcriptData.audioUrl,
        hasAudio: transcriptData.hasAudio,
        seconds_used: transcriptData.seconds_used,
        loadingTranscript: false
      });
    } else {
      selectNote({ 
        ...selectedNote,
        timestamp: originalNote?.timestamp || selectedNote.timestamp || Date.now(),
        time_in_seconds: originalNote?.time_in_seconds || selectedNote.time_in_seconds,
        user_name: originalNote?.user_name || selectedNote.user_name || getUserName(),
        view: 'transcript',
        transcriptionSegments: [],
        loadingTranscript: false
      });
    }
  };

  // ─── Annotation State ────────────────────────────────────────────────
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  const [annotationSegments, setAnnotationSegments] = useState([]);
  const [annotationSaveStatus, setAnnotationSaveStatus] = useState({});
  const [annotationSavingAll, setAnnotationSavingAll] = useState(false);
  const [loadingAnnotation, setLoadingAnnotation] = useState(false);
  const annotationAudioRef = useRef(null);
  const [annotationPlayingDivId, setAnnotationPlayingDivId] = useState(null);
  const [annotationPausedDivId, setAnnotationPausedDivId] = useState(null);
  const annotationPlayIntervalRef = useRef(null);

  // Enter annotation mode
  const handleEnterAnnotation = async () => {
    if (!selectedNote?.session_id) {
      showNotification('No transcript segments to annotate.', 'warning');
      return;
    }
    // Fetch fresh transcript data from API to get expected_output
    setIsAnnotationMode(true);
    setLoadingAnnotation(true);
    let freshSegments = selectedNote?.transcriptionSegments || [];
    try {
      const transcriptData = await fetchTranscriptData(selectedNote.session_id, selectedNote.conversation_id, true);
      if (transcriptData && transcriptData.transcriptionSegments && transcriptData.transcriptionSegments.length > 0) {
        freshSegments = transcriptData.transcriptionSegments;
      }
      // Store audioUrl on selectedNote so the play button renders in annotation mode
      if (transcriptData && transcriptData.audioUrl && !selectedNote?.audioUrl) {
        selectNote({
          ...selectedNote,
          audioUrl: transcriptData.audioUrl,
          hasAudio: transcriptData.hasAudio,
          conversation_id: transcriptData.conversation_id || selectedNote.conversation_id,
        });
      }
    } catch (err) {
      // Fall back to existing segments if API fails
    }
    if (freshSegments.length === 0) {
      showNotification('No transcript segments to annotate.', 'warning');
      setIsAnnotationMode(false);
      setLoadingAnnotation(false);
      return;
    }
    const editableSegments = freshSegments
      .filter(seg => (seg.content_type || 'text') !== 'image')
      .map((seg) => {
        const edited = stripHtml(seg.expected_output || seg.full_message || '');
        const original = stripHtml(seg.full_message || '');
        return {
          ...seg,
          editedText: edited,
          originalText: original,
          initialEditedText: edited,
          saved: !!seg.expected_output && edited !== original,
        };
      });
    if (editableSegments.length === 0) {
      showNotification('No text segments to annotate.', 'warning');
      setIsAnnotationMode(false);
      setLoadingAnnotation(false);
      return;
    }
    setAnnotationSegments(editableSegments);
    setLoadingAnnotation(false);
    // Update URL to persist annotate state across refresh
    if (selectedNote?.conversation_id) {
      navigate(`/conversation/${selectedNote.conversation_id}/annotate`, { replace: true });
    }
  };

  // Update a single annotation segment's text
  const handleAnnotationTextChange = (divId, newText) => {
    setAnnotationSegments(prev =>
      prev.map(seg =>
        seg.div_id === divId ? { ...seg, editedText: newText, saved: false, localSaved: false } : { ...seg }
      )
    );
  };

  // Reset a single annotation segment back to its original text (local only)
  const handleRevertAnnotation = (segment) => {
    setAnnotationSegments(prev =>
      prev.map(seg => {
        if (seg.div_id !== segment.div_id) return seg;
        const revertedText = seg.initialEditedText;
        // If reverted text differs from original, keep saved=true so blue highlighting remains
        const stillSaved = revertedText !== seg.originalText;
        return { ...seg, editedText: revertedText, saved: stillSaved, localSaved: false };
      })
    );
  };

  // Play/pause audio for a specific segment
  const handleAnnotationPlaySegment = (segment, index) => {
    const audio = annotationAudioRef.current;
    if (!audio || !selectedNote?.audioUrl) return;

    const isLastSegment = index === annotationSegments.length - 1;

    // ── Compute correct audio boundaries ──
    // Strategy:
    // 1. If both start_time and end_time are available, normalize them by subtracting
    //    the earliest start_time across all segments (to remove any constant offset)
    //    and use them directly. This respects actual speech boundaries including gaps.
    // 2. If only start_time is available, normalize it and play until the next segment's
    //    normalized start_time (or audio end for the last segment).
    // 3. Otherwise fall back to proportional word-count division.
    const audioDuration = (isFinite(audio.duration) && audio.duration > 0) ? audio.duration : null;
    const wordCount = (segment.originalText || segment.full_message || '').split(/\s+/).filter(Boolean).length;

    let startTime;
    let endTime;

    // Collect valid start_times from all segments to compute normalization offset
    const allStartTimes = annotationSegments
      .map(s => s.start_time)
      .filter(t => t != null && typeof t === 'number' && !isNaN(t) && isFinite(t));
    const hasTimestamps = allStartTimes.length > 0;
    const minStartTime = hasTimestamps ? Math.min(...allStartTimes) : 0;

    if (hasTimestamps && segment.start_time != null && segment.end_time != null) {
      // Both timestamps available – normalize by removing the common offset
      startTime = Math.max(0, segment.start_time - minStartTime);
      endTime = segment.end_time - minStartTime;
      // Clamp to audio duration if available
      if (audioDuration != null && endTime > audioDuration) {
        endTime = audioDuration;
      }
      // Ensure valid range
      if (endTime <= startTime) {
        endTime = startTime + 0.5;
      }
    } else if (hasTimestamps && segment.start_time != null) {
      // Only start_time available – play until next segment's start or audio end
      startTime = Math.max(0, segment.start_time - minStartTime);
      const nextSegment = annotationSegments[index + 1];
      if (nextSegment && nextSegment.start_time != null) {
        endTime = nextSegment.start_time - minStartTime;
      } else if (audioDuration != null) {
        endTime = audioDuration;
      } else {
        endTime = null;
      }
    } else {
      // No backend timestamps – fall back to proportional word-count division
      if (audioDuration != null) {
        const totalWords = annotationSegments.reduce((sum, seg) => {
          return sum + (seg.originalText || seg.full_message || '').split(/\s+/).filter(Boolean).length;
        }, 0);
        if (totalWords > 0) {
          let cumulativeWords = 0;
          for (let i = 0; i < index; i++) {
            cumulativeWords += (annotationSegments[i].originalText || annotationSegments[i].full_message || '').split(/\s+/).filter(Boolean).length;
          }
          startTime = (cumulativeWords / totalWords) * audioDuration;
          endTime = ((cumulativeWords + wordCount) / totalWords) * audioDuration;
        } else {
          startTime = 0;
          endTime = audioDuration;
        }
      } else {
        startTime = 0;
        endTime = null;
      }
    }

    // ── Helper: cleanly stop any ongoing playback ──
    const stopExistingPlayback = () => {
      if (annotationPlayIntervalRef.current) {
        cancelAnimationFrame(annotationPlayIntervalRef.current);
        annotationPlayIntervalRef.current = null;
      }
    };

    // ── Helper: start a rAF loop that monitors playback and stops at the boundary ──
    const startMonitoring = (resolvedStartTime, resolvedEndTime) => {
      stopExistingPlayback();
      const tick = () => {
        if (!audio || audio.paused) {
          annotationPlayIntervalRef.current = null;
          setAnnotationPlayingDivId(null);
          setAnnotationPausedDivId(null);
          return;
        }
        if (resolvedEndTime !== null && audio.currentTime >= resolvedEndTime) {
          audio.pause();
          audio.currentTime = resolvedStartTime;
          annotationPlayIntervalRef.current = null;
          setAnnotationPlayingDivId(null);
          setAnnotationPausedDivId(null);
          return;
        }
        annotationPlayIntervalRef.current = requestAnimationFrame(tick);
      };
      annotationPlayIntervalRef.current = requestAnimationFrame(tick);
    };

    // ── Helper: compute the effective end time (may need audio.duration for last segment) ──
    const getEffectiveEnd = () => {
      if (endTime !== null) return endTime;
      if (isLastSegment && isFinite(audio.duration)) return audio.duration;
      return null;
    };

    // ── Helper: seek to a position, wait for the browser to actually land there, then play ──
    const seekAndPlay = (targetTime) => {
      // Ensure the audio data is loaded enough for accurate seeking
      const doSeek = () => {
        // Set the target position – this kicks off an asynchronous seek
        audio.currentTime = targetTime;

        let played = false;
        const onSeeked = () => {
          audio.removeEventListener('seeked', onSeeked);
          if (played) return;
          played = true;
          audio.play().then(() => {
            setAnnotationPlayingDivId(segment.div_id);
            startMonitoring(targetTime, getEffectiveEnd());
          }).catch(() => {});
        };
        audio.addEventListener('seeked', onSeeked);

        // Fallback: if the seeked event never fires (audio already at the exact position),
        // poll with rAF until the browser confirms the position, then play.
        let attempts = 0;
        const pollForSeek = () => {
          if (played) return;
          if (Math.abs(audio.currentTime - targetTime) < 0.05 || attempts > 30) {
            // Position is close enough or we've waited long enough – play now
            audio.removeEventListener('seeked', onSeeked);
            if (!played) {
              played = true;
              audio.play().then(() => {
                setAnnotationPlayingDivId(segment.div_id);
                startMonitoring(targetTime, getEffectiveEnd());
              }).catch(() => {});
            }
            return;
          }
          attempts++;
          requestAnimationFrame(pollForSeek);
        };
        requestAnimationFrame(pollForSeek);
      };

      if (audio.readyState >= 1) {
        doSeek();
      } else {
        const onReady = () => {
          audio.removeEventListener('loadedmetadata', onReady);
          doSeek();
        };
        audio.addEventListener('loadedmetadata', onReady);
      }
    };

    // ── If the same segment is currently paused → resume from the paused position ──
    if (annotationPausedDivId === segment.div_id) {
      stopExistingPlayback();
      setAnnotationPausedDivId(null);
      setAnnotationPlayingDivId(segment.div_id);
      audio.play().then(() => {
        startMonitoring(audio.currentTime, getEffectiveEnd());
      }).catch(() => {});
      return;
    }

    // ── If the same segment is currently playing → pause ──
    if (annotationPlayingDivId === segment.div_id) {
      audio.pause();
      stopExistingPlayback();
      setAnnotationPlayingDivId(null);
      setAnnotationPausedDivId(segment.div_id);
      return;
    }

    // ── Different segment or first play → cleanly stop old, then start fresh ──
    audio.pause();
    stopExistingPlayback();
    setAnnotationPausedDivId(null);
    seekAndPlay(startTime);
  };

  // Cleanup annotation audio on exit
  const cleanupAnnotationAudio = () => {
    const audio = annotationAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setAnnotationPlayingDivId(null);
    setAnnotationPausedDivId(null);
    if (annotationPlayIntervalRef.current) {
      cancelAnimationFrame(annotationPlayIntervalRef.current);
      annotationPlayIntervalRef.current = null;
    }
  };

  // Save all annotations to database sequentially, updating status per segment
  const handleSaveAllAnnotations = async () => {
    const segmentsToSave = annotationSegments.filter(s => s.editedText !== s.initialEditedText);
    if (segmentsToSave.length === 0) {
      showNotification('No changes to save.', 'info');
      return;
    }
    setAnnotationSavingAll(true);
    const userId = getAuthenticatedUserId();
    let successCount = 0;
    let failCount = 0;

    for (const segment of segmentsToSave) {
      try {
        const response = await fetch(`${baseUrl}${configDetails.ANNOTATE}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
          body: JSON.stringify({
            user_id: userId,
            conversation_id: selectedNote.conversation_id,
            session_id: selectedNote.session_id,
            div_id: segment.div_id,
            expected_output: segment.editedText,
          }),
        });
        if (!response.ok) throw new Error(`Failed (${response.status})`);
        const data = await response.json();
        setAnnotationSaveStatus(prev => ({ ...prev, [segment.div_id]: 'saved' }));
        successCount++;
        setAnnotationSegments(prev =>
          prev.map(seg =>
            seg.div_id === segment.div_id
              ? { ...seg, saved: true, localSaved: false, initialEditedText: segment.editedText, annotated_at: data.annotated_at }
              : { ...seg }
          )
        );
        // Clear saved status after 2 seconds so segment looks normal again
        setTimeout(() => {
          setAnnotationSaveStatus(prev => {
            const next = { ...prev };
            delete next[segment.div_id];
            return next;
          });
        }, 2000);
      } catch (err) {
        setAnnotationSaveStatus(prev => ({ ...prev, [segment.div_id]: 'error' }));
        failCount++;
      }
    }
    setAnnotationSavingAll(false);
    if (failCount === 0) {
      showNotification(`All annotations saved (${successCount} segments).`, 'success');
    } else {
      showNotification(`Saved ${successCount}, ${failCount} failed.`, 'warning');
    }
  };

  // Add CSS animations when component mounts
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate__fadeIn {
        animation: fadeIn 0.5s ease forwards;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      /* Modern Audio Player Styles */
      .modern-audio-player {
        padding: 12px 0;
        background-color: #ffffff;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Record context for "live" display
  const {
    isLive: isLiveSession,
    sessionId: liveSessionId,
    transcriptSegments: liveSegments,
    noteTitle: liveTitle,
    timer: liveTimer
  } = useRecord();

  // Helper to get correct duration for selectedNote
  const getNoteDuration = (note) => {
    // If note is inprogress and live, use live_status.time
    if (
      note &&
      note.status === 'inprogress' &&
      note.live_status &&
      note.live_status.current_status === 'LIVE' &&
      typeof note.live_status.time === 'number'
    ) {
      return note.live_status.time;
    }
    // Otherwise, use time_in_seconds or seconds_used
    if (typeof note.time_in_seconds === 'number' && !isNaN(note.time_in_seconds)) {
      return note.time_in_seconds;
    }
    if (typeof note.seconds_used === 'number' && !isNaN(note.seconds_used)) {
      return note.seconds_used;
    }
    return 0;
  };

  const formatTimestamp = (timestamp) => {    
    let date;
    
    // At this point, timestamp should be a number (milliseconds)
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      console.error('❌ formatTimestamp: Expected number, got:', typeof timestamp, 'value:', timestamp);
      date = new Date(); // fallback
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('❌ formatTimestamp: Invalid date created from timestamp:', timestamp);
      date = new Date(); // fallback
    }

    // Display dates/times in IST (Asia/Kolkata)
    const optionsDate = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Kolkata' };
    const optionsTime = { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: 'Asia/Kolkata' };
    
    const result = {
      dateString: date.toLocaleDateString('en-US', optionsDate),
      timeString: date.toLocaleTimeString('en-US', optionsTime),
    };
    
    return result;
  };

  useEffect(() => {
    const userId = getAuthenticatedUserId();
    
    logger.info('MainHome component mounted', {
      component: COMPONENT_NAME,
      action: 'COMPONENT_MOUNT',
      userId,
      context: {
        pathname: location.pathname,
        conversationId: conversationId || null,
        isLiveSession,
        liveSessionId: liveSessionId || null,
        browserOnline: navigator.onLine,
        timestamp: Date.now()
      },
      includeBrowserInfo: true
    });

    if (!userId) {
      logger.warn('User not authenticated, redirecting to login', {
        component: COMPONENT_NAME,
        action: 'REDIRECT_TO_LOGIN',
        context: { reason: 'missing_user_id', timestamp: Date.now() }
      });
      navigate(routesfe.LOGIN);
    }

    return () => {
      logger.info('MainHome component unmounted', {
        component: COMPONENT_NAME,
        action: 'COMPONENT_UNMOUNT',
        userId: getAuthenticatedUserId(),
        context: {
          notesCount: notes?.length || 0,
          selectedNoteId: selectedNote?.session_id || null,
          timestamp: Date.now()
        }
      });
    };
  }, [navigate]);

  // Reset selectedNote when navigating to home route
  useEffect(() => {
    if (location.pathname === routesfe.HOME || location.pathname === '/') {
      deselectNote();
    }
  }, [location.pathname, deselectNote]);

  // Function to check if notes have associated audio files
  const fetchNotesWithAudioStatus = async (notes) => {
    if (!notes.length) return [];
    // Since GET_SUMMARIES endpoint is removed, we'll return notes without audio status
    // Audio functionality will need to be handled differently or removed
    return notes.map(note => ({
      ...note,
      hasAudio: false,
      s3_path: null,
      audioPath: null,
      audioUrl: null,
      summary: null,
      transcription: null,
    }));
  };

  useEffect(() => {
    if (location.pathname.startsWith('/conversation/')) {
      setLoading(false);
      return;
    }

    const user = getAuthenticatedUserDetails();
    const userId = user && (user.cognito_user_id || user.userSub);
    if (!userId) {
      logger.warn('Cannot fetch notes - user not authenticated', {
        component: COMPONENT_NAME,
        action: 'FETCH_NOTES_AUTH_ERROR',
        context: { pathname: location.pathname }
      });
      setNotes([]);
      setLoading(false);
      showNotification('Error: Missing user_id. Please log in again.', 'error');
      return;
    }

    logger.info('Fetching notes list', {
      component: COMPONENT_NAME,
      action: 'FETCH_NOTES_START',
      userId,
      context: {
        currentPage,
        pathname: location.pathname,
        timestamp: Date.now()
      }
    });

    setLoading(true);
      // Fetch notes for the current page with limit=10
     fetch(`${baseUrl}${META_DATA}?user_id=${userId}&page=${currentPage}&limit=10`, 
      { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } })
      .then((res) => res.json())
      .then(async (metaData) => {
        //  Cache conversation_id -> session_id mapping to avoid META_DATA call on refresh
        if (Array.isArray(metaData.records)) {
          metaData.records.forEach(record => {
            if (record.conversation_id && record.session_id) {
              const cacheKey = `conv_session_${record.conversation_id}`;
              const existingValue = localStorage.getItem(cacheKey);
              
              // Only write if data is different or doesn't exist
              if (existingValue !== record.session_id) {
                localStorage.setItem(cacheKey, record.session_id);
              }
            }
          });
        }
        
        // Set pagination info from backend
        setFirstPage(metaData.first_page_number || 1);
        setLastPage(metaData.last_page_number || 1);
        // Step 2: Create notes from meta_data response
        if (!Array.isArray(metaData.records) || metaData.records.length === 0) {
          setNotes([]);
          setLoading(false);
          return;
        }

        const filteredRecords = metaData.records.filter((meta) => {
          const liveStatus = meta?.live_status?.current_status;
          // Hide abandoned/stopped-but-not-completed recordings
          if (liveStatus === 'STOPPED' && meta.status !== 'completed') {
            return false;
          }
          // Also hide anything explicitly marked discarded
          if (meta.status === 'discarded' || meta.status === 'abandoned') {
            return false;
          }
          return true;
        });

        if (filteredRecords.length === 0) {
          setNotes([]);
          setLoading(false);
          return;
        }
        
        // Get active conversation from cookies
        const activeCookie = getCookie('conversation_active');
        const cookieConvId = getCookie('conversation_id');
        const isActiveSession = activeCookie === 'true' && cookieConvId;
        
        const notesFromMetaData = filteredRecords.map((meta, index) => {          
          let timestamp = meta.timestamp;
          if (timestamp && typeof timestamp === 'string') {
            const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
            const utcDate = new Date(utcTimestamp);
            if (!isNaN(utcDate.getTime())) {
              timestamp = utcDate.getTime();
            } else {
              console.error(`❌ Failed to parse UTC timestamp: "${timestamp}"`);
              timestamp = Date.now();
            }
          } else {
            console.warn(`⚠️ Unexpected timestamp format:`, timestamp);
            timestamp = Date.now();
          }
          
          // Determine if this note is live/ongoing
          const isLiveNote = (isLiveSession && meta.session_id === liveSessionId) || 
                            (isActiveSession && meta.conversation_id === cookieConvId) ||
                            (meta.status === 'created' || meta.status === 'inprogress');
          
          return {
            session_id: meta.session_id,
            conversation_id: meta.conversation_id || '',
            timestamp: timestamp,
            time_in_seconds: meta.seconds_used,
            user_name: getUserName(),
            status: meta.status,
            summary: null,
            content: '',
            transcription: '',
            audioUrl: null,
            hasAudio: false,
            s3_path: null,
            audioPath: null,
            live: isLiveNote // Mark as live if matches any live condition
          };
        });
        
        logger.info('Notes list fetched successfully', {
          component: COMPONENT_NAME,
          action: 'FETCH_NOTES_SUCCESS',
          userId,
          context: {
            notesCount: notesFromMetaData.length,
            currentPage,
            firstPage: metaData.first_page_number || 1,
            lastPage: metaData.last_page_number || 1,
            timestamp: Date.now()
          }
        });

        setNotes(notesFromMetaData);
        setLoading(false);
      })
      .catch(err => {
        logger.error('Failed to fetch notes list', {
          component: COMPONENT_NAME,
          action: 'FETCH_NOTES_ERROR',
          userId: getAuthenticatedUserId(),
          error: err,
          context: { currentPage, timestamp: Date.now() }
        });
        setNotes([]);
        setLoading(false);
      });
  }, [homeReset, currentPage, isLiveSession, liveSessionId, location.pathname]);


  // On mount or when conversationId changes, load selected note if conversationId is present
  useEffect(() => {
    // Parse conversationId and view from URL
    let urlConversationId = conversationId;
    let urlView = 'transcript'; // default view
    
    // Check if we're on the new URL format: /conversation/:conversationId/transcript or /conversation/:conversationId/summary
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'conversation' && pathParts[3]) {
      urlConversationId = pathParts[2];
      urlView = pathParts[3]; // 'transcript' or 'summary'
    }
    
    if (urlConversationId) {
      // Reset annotation mode when navigating to a different note via route change
      if (isAnnotationMode && (!selectedNote || selectedNote.conversation_id !== urlConversationId)) {
        cleanupAnnotationAudio();
        setIsAnnotationMode(false);
        setAnnotationSegments([]);
        setAnnotationSaveStatus({});
      }

      if (selectedNote && selectedNote.conversation_id === urlConversationId && selectedNote.view === urlView) {
        return;
      }

      if (location.pathname.startsWith('/conversation/') && !urlConversationId) {
        return;
      }
      
      // Find the note in notes or create a basic note structure with API integration
      const loadNote = async () => {
        const user = getAuthenticatedUserDetails();
        const userId = user && user.userSub;
        if (!userId) return;

        const existingNote = notes.find(note => note.conversation_id === urlConversationId);
        const sessionId =
          notes.find(n => n.conversation_id === urlConversationId)?.session_id ||
          localStorage.getItem(`conv_session_${urlConversationId}`);
        
        if (!sessionId) {
          // Don't show error if notes array is empty (still loading)
          if (notes.length === 0) {
            return;
          }
          showNotification('Session information not found for this note.', 'error');
          return;
        }
        
        // Use URL view instead of localStorage for view preference
        const viewToUse = urlView === 'annotate' ? 'transcript' : urlView;
        const isAnnotateView = urlView === 'annotate';
        // Update localStorage to match URL
        localStorage.setItem(`note_view_${sessionId}`, viewToUse);
        
        const initialNote = {
          session_id: sessionId,
          conversation_id: urlConversationId,
          summary: 'Loading summary...',
          view: viewToUse,
          hasAudio: false,
          audioUrl: null,
          audioPath: null,
          s3_path: null,
          seconds_used: null,
          timestamp: existingNote?.timestamp || Date.now(),
          time_in_seconds: existingNote?.time_in_seconds || null,
          user_name: existingNote?.user_name || selectedNote?.user_name || getUserName(),
          status: existingNote?.status || 'completed', // Include status from existing note or default to completed
          transcriptionSegments: null,
          loadingTranscript: viewToUse === 'transcript' && !isAnnotateView,
          loadingSummary: viewToUse === 'summary'
        };
        selectNote(initialNote);
        // Set annotation loading state for annotate URL
        if (isAnnotateView) {
          setLoadingAnnotation(true);
        }

        // Fetch data based on the view from URL
        // For 'annotate' view, we need transcript data (same as transcript view)
        if (viewToUse === 'summary' && !isAnnotateView) {
          // If view is summary, fetch summary data (try regardless of status since API is responding)
          const summaryData = await fetchSummaryData(sessionId);
          
          if (summaryData) {
            selectNote({
              ...initialNote,
              conversation_id: urlConversationId,
              summary: summaryData.summary,
              status: existingNote?.status || 'completed', // Preserve status
              loadingSummary: false
            });
          } else {
            // If API fails, show appropriate message based on status
            const errorMessage = (existingNote?.status === 'completed') 
              ? 'Failed to load summary.' 
              : 'Summary will take time to generate.';
            selectNote({
              ...initialNote,
              conversation_id: urlConversationId,
              summary: errorMessage,
              status: existingNote?.status || 'completed',
              loadingSummary: false
            });
          }
        } else {
          // If view is transcript or annotate, fetch transcript data
          const transcriptData = await fetchTranscriptData(sessionId, urlConversationId);
          
          if (transcriptData) {
            const updatedNote = {
              ...initialNote,
              conversation_id: transcriptData.conversation_id || urlConversationId,
              transcriptionSegments: transcriptData.transcriptionSegments,
              audioUrl: transcriptData.audioUrl,
              hasAudio: transcriptData.hasAudio,
              seconds_used: transcriptData.seconds_used,
              time_in_seconds: transcriptData.seconds_used || initialNote.time_in_seconds,
              status: existingNote?.status || 'completed', // Preserve status
              loadingTranscript: false
            };
            selectNote(updatedNote);
            // If URL was annotate view, enter annotation mode after loading transcript
            if (isAnnotateView && transcriptData.transcriptionSegments && transcriptData.transcriptionSegments.length > 0) {
              const editableSegments = transcriptData.transcriptionSegments
                .filter(seg => (seg.content_type || 'text') !== 'image')
                .map((seg) => {
                  const edited = stripHtml(seg.expected_output || seg.full_message || '');
                  const original = stripHtml(seg.full_message || '');
                  return {
                    ...seg,
                    editedText: edited,
                    originalText: original,
                    initialEditedText: edited,
                    saved: !!seg.expected_output && edited !== original,
                  };
                });
              if (editableSegments.length > 0) {
                setAnnotationSegments(editableSegments);
                setIsAnnotationMode(true);
              }
              setLoadingAnnotation(false);
            }
          } else {
            selectNote({
              ...initialNote,
              conversation_id: urlConversationId,
              transcriptionSegments: [],
              status: existingNote?.status || 'completed', // Preserve status
              loadingTranscript: false
            });
          }
        }
      };
      loadNote();
    } else {
      deselectNote();
    }
  }, [conversationId, location.pathname, notes]);

  // Polling effect for summary when status is processing
  useEffect(() => {
    if (
      selectedNote &&
      selectedNote.view === 'summary' &&
      selectedNote.status === 'processing' &&
      !selectedNote.loadingSummary
    ) {
      pollingRef.current = setInterval(async () => {
        try {
          const summaryData = await fetchSummaryData(selectedNote.session_id);
          // Find the latest status from notes array
          const originalNote = notes.find(note => note.session_id === selectedNote.session_id);
          const latestStatus = originalNote?.status || selectedNote.status;

          if (
            summaryData &&
            summaryData.summary &&
            summaryData.summary.trim() !== '' &&
            summaryData.summary.trim().toLowerCase() !== 'no summary available' &&
            summaryData.summary.length > 10
          ) {
            // Summary is ready, update and stop polling
            selectNote({
              ...selectedNote,
              summary: summaryData.summary,
              status: 'completed',
              loadingSummary: false,
            });
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          } else if (latestStatus === 'completed') {
            // Status updated to completed, stop polling
            selectNote({
              ...selectedNote,
              summary: summaryData?.summary || 'Failed to load summary.',
              status: 'completed',
              loadingSummary: false,
            });
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          // else: keep polling
        } catch (error) {
          console.error('Error during summary polling:', error);
        }
      }, POLLING_INTERVAL);
    }

    // Cleanup polling on tab switch or unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [selectedNote?.view, selectedNote?.status, selectedNote?.session_id, selectedNote?.loadingSummary, notes]);

  // Update handleNoteClick to use navigate for routing
  const handleNoteClick = async (note) => {
    const userId = getAuthenticatedUserId();
    
    logger.info('Note clicked', {
      component: COMPONENT_NAME,
      action: 'CLICK_NOTE',
      userId,
      context: {
        sessionId: note.session_id,
        conversationId: note.conversation_id || null,
        noteStatus: note.status,
        isLive: note.live || false,
        pathname: location.pathname,
        timestamp: Date.now()
      }
    });

    // Reset annotation mode when switching to a different note
    if (isAnnotationMode) {
      cleanupAnnotationAudio();
      setIsAnnotationMode(false);
      setAnnotationSegments([]);
      setAnnotationSaveStatus({});
    }
    
    // FIRST CHECK: If note status is 'processing' or 'completed', go directly to summary view
    // This takes priority over any cookie-based or live flag checks
    if (note.status === 'processing' || note.status === 'completed') {
      if (!note.conversation_id) {
        logger.warn('Missing conversation ID for note', {
          component: COMPONENT_NAME,
          action: 'NOTE_CLICK_ERROR',
          userId,
          context: { sessionId: note.session_id, noteStatus: note.status }
        });
        showNotification('Missing session info for this note.', 'error');
        return;
      }
      logger.debug('Navigating to summary view for completed note', {
        component: COMPONENT_NAME,
        action: 'NAVIGATE_TO_SUMMARY',
        userId,
        context: { conversationId: note.conversation_id, noteStatus: note.status }
      });
      navigate(`/conversation/${note.conversation_id}/transcript`);
      return;
    }
    
    // Check if note has 'live' flag set
    // Helper function to validate and navigate for potentially live sessions
    const validateAndNavigateToRecord = (conversationId) => {
      if (!conversationId || !userId) {
        navigate(routesfe.RECORD);
        return;
      }
      
      // Check if session is still live using existing notes data
      const { isLive: isSessionLive, status: sessionStatus } = checkSessionIsLive(conversationId, notes);
      
      if (isSessionLive) {
        // Session is still live, navigate to record
        navigate(`/record/${conversationId}`);
      } else {
        // Clear stale cookies
        clearSessionCookies();
        
        // Navigate to the completed note view instead
        if (!note.conversation_id) {
          navigate(`/conversation/${note.conversation_id}/transcript`);
        } else {
          navigate(routesfe.HOME);
        }
        
        // Show notification to user
        showNotification('This session has ended. Showing the saved note.', 'info');
      }
    };
    
    // NEW: Check if note status is 'created' or 'inprogress' (ongoing session)
    if (note.status === 'created' || note.status === 'inprogress') {
      const conversationId = note.conversation_id || getCookie('conversation_id');
       validateAndNavigateToRecord(conversationId);
      return;
    }
    
    // Check if there's an active live session matching this note
    if ((isLiveSession && liveSessionId && note.session_id === liveSessionId) || note.live) {
      const conversationId = note.conversation_id || getCookie('conversation_id') || liveSessionId;
      validateAndNavigateToRecord(conversationId);
      return;
    }
    
    // NEW: Check if there's an active conversation in cookies matching this note
    const activeCookie = getCookie('conversation_active');
    const cookieConvId = getCookie('conversation_id');
    if (activeCookie === 'true' && cookieConvId && note.conversation_id === cookieConvId) {
      validateAndNavigateToRecord(cookieConvId);
      return;
    }
    
    // For completed notes, use conversation_id instead of session_id
    if (!note.conversation_id) {
      showNotification('Missing conversation info for this note.', 'error');
      return;
    }
    
    // Navigate using conversation_id for completed notes with transcript as default view
    navigate(`/conversation/${note.conversation_id}/transcript`);
  };

  // Delete note handler with custom confirmation and notifications
  const handleDeleteNote = async (noteToDelete, event) => {
    // Prevent event bubbling to note click handler
    event.stopPropagation();
    const userId = getAuthenticatedUserId();

    logger.info('Delete note requested', {
      component: COMPONENT_NAME,
      action: 'CLICK_DELETE_NOTE',
      userId,
      context: {
        sessionId: noteToDelete.session_id,
        conversationId: noteToDelete.conversation_id || null,
        noteStatus: noteToDelete.status,
        timestamp: Date.now()
      }
    });

    if (isLiveSession && noteToDelete.session_id === liveSessionId) {
      logger.warn('Cannot delete live session note', {
        component: COMPONENT_NAME,
        action: 'DELETE_NOTE_BLOCKED',
        userId,
        context: { sessionId: noteToDelete.session_id, reason: 'live_session' }
      });
      showNotification('Cannot delete a note that is currently being recorded.', 'warning');
      return;
    }

    // Show custom confirmation modal
    setConfirmModal({
      isOpen: true,
      noteToDelete: noteToDelete
    });
  };

  // Handle confirmation modal confirm
  const handleConfirmDelete = async () => {
    const noteToDelete = confirmModal.noteToDelete;
    setConfirmModal({ isOpen: false, noteToDelete: null });

    const userId = getAuthenticatedUserId();
    if (!userId) {
      logger.error('Delete failed - user not authenticated', {
        component: COMPONENT_NAME,
        action: 'DELETE_NOTE_AUTH_ERROR',
        context: { sessionId: noteToDelete.session_id }
      });
      showNotification('Error: Unable to authenticate user. Please log in again.', 'error');
      return;
    }

    logger.info('Confirming note deletion', {
      component: COMPONENT_NAME,
      action: 'DELETE_NOTE_CONFIRM',
      userId,
      context: {
        sessionId: noteToDelete.session_id,
        conversationId: noteToDelete.conversation_id || null,
        timestamp: Date.now()
      }
    });

    setDeletingNotes(prev => new Set(prev).add(noteToDelete.session_id));

    try {
      const response = await fetch(`${baseUrl}${DELETE_NOTE}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
        body: JSON.stringify({
          user_id: userId,
          session_id: noteToDelete.session_id
        }),
      });

      // Parse the response to check for backend errors
      const responseData = await response.json().catch(() => ({}));

      // Check if backend returned an error (even with 200 status)
      if (!response.ok || responseData.error) {
        const errorMessage = responseData.error || responseData.message || `Failed to delete note (${response.status})`;
        throw new Error(errorMessage);
      }

      logger.info('Note deleted successfully', {
        component: COMPONENT_NAME,
        action: 'DELETE_NOTE_SUCCESS',
        userId,
        context: {
          sessionId: noteToDelete.session_id,
          conversationId: noteToDelete.conversation_id || null,
          timestamp: Date.now()
        }
      });

      setNotes(prevNotes => {
        const updatedNotes = prevNotes.filter(note => note.session_id !== noteToDelete.session_id);

        // Handle pagination adjustment immediately with the updated notes
        const totalPages = Math.ceil(updatedNotes.length / NOTES_PER_PAGE);
        if (currentPage > totalPages && totalPages > 0) {
          setCurrentPage(totalPages);
        }

        return updatedNotes;
      });

      // If deleted note is currently selected, deselect it
      if (selectedNote && selectedNote.session_id === noteToDelete.session_id) {
        deselectNote();
      }

      // Show success notification
      showNotification(`Note ${noteToDelete.dayNoteNumber} deleted successfully.`, 'success');

    } catch (error) {
      logger.error('Delete note failed', {
        component: COMPONENT_NAME,
        action: 'DELETE_NOTE_ERROR',
        userId,
        error,
        context: {
          sessionId: noteToDelete.session_id,
          timestamp: Date.now()
        }
      });
      showNotification(`Failed to delete note: ${error.message}`, 'error', 6000);

      // Force refresh notes from backend to ensure UI is in sync
      // This handles the case where the note might have been deleted elsewhere
      triggerHomeReset();
    } finally {
      // Remove from deleting state
      setDeletingNotes(prev => {
        const newSet = new Set(prev);
        newSet.delete(noteToDelete.session_id);
        return newSet;
      });
    }
  };

  // Handle confirmation modal cancel
  const handleCancelDelete = () => {
    setConfirmModal({ isOpen: false, noteToDelete: null });
  };

  // Handle feedback modal
  const handleOpenFeedback = () => {
    logger.info('Feedback modal opened', {
      component: COMPONENT_NAME,
      action: 'CLICK_OPEN_FEEDBACK',
      userId: getAuthenticatedUserId(),
      context: { timestamp: Date.now() }
    });
    setFeedbackModal({ isOpen: true });
  };

  const handleCloseFeedback = () => {
    logger.debug('Feedback modal closed', {
      component: COMPONENT_NAME,
      action: 'CLOSE_FEEDBACK',
      userId: getAuthenticatedUserId()
    });
    setFeedbackModal({ isOpen: false });
  };

  const handleFeedbackSubmit = () => {
    logger.info('Feedback submitted', {
      component: COMPONENT_NAME,
      action: 'SUBMIT_FEEDBACK',
      userId: getAuthenticatedUserId(),
      context: { timestamp: Date.now() }
    });
    setFeedbackModal({ isOpen: false });
  };

  // Render notes (with live note on top if exists)
  function renderPaginatedNotes() {
    // Group notes by date
    const groupedEntries = Object.entries(
      notes.reduce((acc, note) => {
        const { dateString, timeString } = formatTimestamp(note.timestamp);
        const key = dateString;
        const extendedNote = { ...note, dateString, timeString };
        if (!acc[key]) acc[key] = [];
        acc[key].push(extendedNote);
        return acc;
      }, {})
    ).sort((a, b) => new Date(b[1][0].timestamp) - new Date(a[1][0].timestamp));

    // Add dayNoteNumber for display (descending order)
    // Handler for Record button click (before starting recording)
    const handleRecordClick = async () => {
      try {
        const user = getAuthenticatedUserDetails();
        const userId = user && (user.cognito_user_id || user.userSub);
        if (!userId) {
          showNotification("Error: Missing user_id. Please log in again.", "error");
          return;
        }
        // 1️⃣ Call conversation_initiated API
        const conversationResponse = await fetch(`${baseUrl}/conversation_initiated`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": authIdToken },
          body: JSON.stringify({
            user_id: userId,
            status: "created"
          }),
        });
        if (!conversationResponse.ok) throw new Error("Failed to create conversation");
        const conversationData = await conversationResponse.json();
        const { conversation_id, session_id } = conversationData;
        // 2️⃣ Call meta_data API
        await fetch(`${baseUrl}/meta_data`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": authIdToken },
          body: JSON.stringify({
            user_id: userId,
            session_id,
            conversation_id,
            status: "created"
          }),
        });
        // 3️⃣ Save session info and navigate
        localStorage.setItem("session_id", session_id);
        localStorage.setItem("session_status", "created");
        navigate(`/record/${session_id}`);
      } catch (error) {
        showNotification("Failed to initialize recording metadata", "error");
      }
    };
    // Remove the ongoing note from the regular notes list
    let filteredNotes = notes;


    const groupedEntriesFiltered = Object.entries(
      filteredNotes.reduce((acc, note) => {
        const { dateString, timeString } = formatTimestamp(note.timestamp);
        const key = dateString;
        const extendedNote = { ...note, dateString, timeString };
        if (!acc[key]) acc[key] = [];
        acc[key].push(extendedNote);
        return acc;
      }, {})
    ).sort((a, b) => new Date(b[1][0].timestamp) - new Date(a[1][0].timestamp));

    const allNotes = groupedEntriesFiltered.flatMap(([date, notes]) => {
      const sorted = [...notes].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      const total = sorted.length;
      return sorted.map((note, idx) => ({ ...note, dayNoteNumber: total - idx }));
    });

    // No frontend slicing: notes already paginated from backend
    const groupedPaginated = allNotes.reduce((acc, note) => {
      if (!acc[note.dateString]) acc[note.dateString] = [];
      acc[note.dateString].push(note);
      return acc;
    }, {});

    const loggedInUserName = getUserName();
    const userInitial = loggedInUserName.charAt(0).toUpperCase();

    // Live note object for display
    let ongoingNoteDetails = null;
    if (isLiveSession && liveSessionId && Array.isArray(notes)) {
      ongoingNoteDetails = notes.find(n => n.session_id === liveSessionId && n.status === 'created');
    }

    const liveNoteBlock = isLiveSession && ongoingNoteDetails ? (
      <div
        key="live-note"
        className="bg-white p-4 rounded-3 shadow-sm mb-4 border border-primary"
        style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', cursor: 'pointer', border: '2px solid #0d6efd', background: '#f8f9fa' }}
        onClick={() => {
          // For live sessions, navigate to /record/conversation_id if conversation_id exists in cookies
           const conversationId = ongoingNoteDetails.conversation_id || getCookie('conversation_id');
          if (conversationId) {
            navigate(`/record/${conversationId}`);
          } else {
            navigate(routesfe.RECORD);
          }
        }}
      >
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
          style={{ width: '40px', height: '40px', fontWeight: 'bold', fontSize: '1rem' }}
        >
          LIVE
        </div>
        <div className="flex-grow-1" style={{ paddingRight: '40px' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold text-primary mb-0">
              {ongoingNoteDetails.user_name || liveTitle || 'Untitled Note'} (Recording...)
            </h6>
          </div>
          <div className="text-muted mb-1" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span role="img" aria-label="user">👤</span> {ongoingNoteDetails.user_name}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span role="img" aria-label="clock">⏰</span> <strong>{formatTimestamp(ongoingNoteDetails.timestamp).timeString}</strong>
            </span>
            {ongoingNoteDetails.time_in_seconds !== undefined && ongoingNoteDetails.time_in_seconds !== null && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <span role="img" aria-label="duration">⏱️</span> {formatNoteTime(ongoingNoteDetails.time_in_seconds)}
              </span>
            )}
            {ongoingNoteDetails.status === 'created' && (
              <span className="badge bg-info-subtle text-info ms-2">{ongoingNoteDetails.status}</span>
            )}
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        {liveNoteBlock}
        {Object.entries(groupedPaginated).map(([date, notes]) => (
          <div key={date} className="mb-4">
            <h6 className="text-primary fw-bold mb-3">📅 {date}</h6>
            {notes.map((note) => {
              const isDeleting = deletingNotes.has(note.session_id);
              return (
                <div
                  key={note.session_id}
                  onClick={() => !isDeleting && handleNoteClick(note)}
                  className="bg-white p-4 rounded-3 shadow-sm mb-3 border border-gray-200 transition-all hover:shadow"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    transform: 'translateY(0)',
                    opacity: isDeleting ? 0.6 : 1,
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    if (!isDeleting) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 1px 6px -2px rgba(0, 0, 0, 0.05)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isDeleting) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                    }
                  }}
                >
                  {/* Delete Button - positioned absolutely in top-right */}
              <button
                    onClick={(e) => handleDeleteNote(note, e)}
                    disabled={isDeleting}
                    className="btn btn-sm p-2"
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'transparent',
                      border: 'none',
                      color: '#6c757d',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      transition: 'all 0.2s ease',
                      opacity: isDeleting ? 0.5 : 0.7,
                      zIndex: 10
                    }}
                    onMouseOver={(e) => {
                      if (!isDeleting) {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.color = '#dc2626';
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isDeleting) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#6c757d';
                        e.currentTarget.style.opacity = '0.7';
                      }
                    }}
                    title={isDeleting ? 'Deleting...' : 'Delete note'}
                  >
                    {isDeleting ? (
                      <div
                        style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid #dc2626',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                    ) : (
                      <BsTrash size={14} />
                    )}
                  </button>
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style={{ width: '40px', height: '40px', fontWeight: 'bold', fontSize: '1rem' }}
                  >
                    {userInitial}
                  </div>
                  <div className="flex-grow-1" style={{ paddingRight: '40px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold text-primary mb-0">
                        Note {note.dayNoteNumber}
                      </h6>
                    </div>

                    <div className="text-muted mb-1" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span role="img" aria-label="user">👤</span> {note.user_name || loggedInUserName}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span role="img" aria-label="clock">⏰</span> <strong>{note.timeString}</strong>
                      </span>
                      {note.time_in_seconds !== undefined && note.time_in_seconds !== null && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <span role="img" aria-label="duration">⏱️</span> {formatNoteTime(note.time_in_seconds)}
                        </span>
                      )}
                      {note.status === 'inprogress' && (
                        <span className="badge bg-success-subtle text-success ms-2">Live recording...</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </>
    );
  }

  const isNoteOpen = !!(selectedNote && conversationId);

  // The Share control is always available on an opened note — revocation
  // eligibility (can_revoke / public_access_revoked) is resolved inside the
  // Share popup via GET /public_access/{conversation_id}, so there is no
  // dependency on conversation completion or summary generation.

  return (
    <div className="mainhome-root d-flex" style={{ overflow: 'hidden', minWidth: 0 }}>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
          duration={notification.duration}
        />
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Note"
        message={confirmModal.noteToDelete ?
          `Are you sure you want to delete "Note ${confirmModal.noteToDelete.dayNoteNumber}"? This action cannot be undone.` :
          ''
        }
        confirmText="Delete"
        cancelText="Cancel"
      />

      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={handleCloseFeedback}
        onSubmit={handleFeedbackSubmit}
        showNotification={showNotification}
        authIdToken={authIdToken}
      />

      {/* Share Conversation popup — same component as the recording page.
          Also hosts the Revoke Public Access action (owner-confirmed). */}
      {conversationId && (
        <ShareModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareLink={`${window.location.origin}/watch/${conversationId}`}
          conversationId={conversationId}
          authIdToken={authIdToken}
          initialStatus={publicAccess}
          onStatusChange={setPublicAccess}
        />
      )}

      <div
        className={`container-fluid bg-light flex-grow-1 d-flex flex-column ${isNoteOpen ? 'p-0' : 'px-2 pt-1 pb-2'}`}
        style={{ minWidth: 0, ...(isNoteOpen ? { height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0 } : {}) }}
      >
        {!isNoteOpen && (
          <TopNavbar
            authIdToken={authIdToken}
            onToggleRightSidebar={toggleRightSidebar}
            rightSidebarVisible={rightSidebarVisible}
          />
        )}
        <div
          className="d-flex flex-column flex-grow-1 mainhome-content-scroll"
          style={{
            height: isNoteOpen ? '100%' : 'calc(100vh - 60px)',maxHeight: isNoteOpen ? '100%' : undefined,padding: isNoteOpen ? '0' : '10px',overflow: isNoteOpen ? 'hidden' : 'auto',minHeight: 0,minWidth: 0,overflowX: 'hidden',
          }}
        >
          {selectedNote && conversationId ? (
            <div               className="p-4 rounded-3 shadow-lg border border-gray-300 mainhome-summary-container animate__animated animate__fadeIn"
              style={{
                maxWidth: '100%',width: '100%',height: '100%',maxHeight: '100%',minHeight: 0,minWidth: 0,overflow: 'hidden',display: 'flex',flexDirection: 'column',
              }}
            >
              <div className="mb-4 d-flex align-items-center">
                <button
                  onClick={() => navigate(routesfe.HOME)}
                  className="btn btn-link p-0 me-3 text-dark"
                  style={{ fontSize: '1.5rem', cursor: 'pointer', background: 'none', border: 'none' }}
                  title="Back to home"
                >
                  <BsArrowLeft />
                </button>
                <h1 className="fw-bold text-dark m-0">Note</h1>
                {/* Reuse the same circular Share control as the recording page */}
                <button
                  className="btn btn-outline-primary d-flex align-items-center ms-auto share-button-mobile"
                  style={{ borderRadius: '50%', width: 44, height: 44 }}
                  title="Share Conversation"
                  onClick={() => setShowShareModal(true)}
                >
                  <BsShareFill style={{ fontSize: '1.4rem' }} />
                </button>
              </div>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center min-width-0">
                  <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                       style={{ width: '36px', height: '36px', fontWeight: 'bold', fontSize: '1rem' }}>
                    {getUserName().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="fw-bold mb-1">{getUserName() || 'Unknown User'}</div>
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                      <div className="d-flex align-items-center gap-3">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <span role="img" aria-label="calendar">📅</span> {formatTimestamp(selectedNote.timestamp).dateString}
                        </span>
                        {selectedNote.time_in_seconds !== undefined && selectedNote.time_in_seconds !== null && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span role="img" aria-label="clock">⏱️</span> {formatNoteTime(getNoteDuration(selectedNote))}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <div className="mb-4 border-bottom d-flex">
                <button
                  className={`btn me-4 pb-2 ${
                    selectedNote.view === 'summary' && !isAnnotationMode
                      ? 'border-bottom border-2 border-primary text-primary fw-bold'
                      : 'text-muted'
                  }`}
                  style={{ background: 'none', border: 'none', fontSize: '1.1rem' }}
                  onClick={handleSummaryTabClick}
                >
                  Summary
                </button>
                <button
                  className={`btn me-4 pb-2 ${
                    selectedNote.view === 'transcript' && !isAnnotationMode
                      ? 'border-bottom border-2 border-primary text-primary fw-bold'
                      : 'text-muted'
                  }`}
                  style={{ background: 'none', border: 'none', fontSize: '1.1rem' }}
                  onClick={handleTranscriptTabClick}
                >
                  Transcript
                </button>
                <button
                  className={`btn pb-2 ${
                    isAnnotationMode
                      ? 'border-bottom border-2 border-primary text-primary fw-bold'
                      : 'text-muted'
                  }`}
                  style={{ background: 'none', border: 'none', fontSize: '1.1rem' }}
                  onClick={handleEnterAnnotation}
                >
                  Annotate
                </button>
              </div>
              {selectedNote.view === 'summary' && !isAnnotationMode && (
                <>
                  <div className="summary-view-wrapper">
                    <div className="summary-scroll-area thin-scrollbar">
                     {selectedNote.loadingSummary ? (
                        <div className="summary-loading-container">
                          <div className="summary-loading-spinner" />
                          <h6 className="summary-loading-title">
                            Generating Summary
                          </h6>
                          <p className="summary-loading-description">
                            AI is analyzing your notes...
                          </p>
                        </div>
                      ) : (selectedNote.summary && 
                           selectedNote.summary.trim() !== '' && 
                           selectedNote.summary !== 'Loading summary...' &&
                           selectedNote.summary !== 'Summary will take time to generate.' &&
                           selectedNote.summary !== 'Failed to load summary.' &&
                           selectedNote.summary !== 'No summary available' &&
                           selectedNote.summary.length > 10) || 
                          selectedNote.status === 'completed' ? (
                        <>
                          <span>{selectedNote.summary && 
                                 selectedNote.summary !== 'Loading summary...' &&
                                 selectedNote.summary !== 'Summary will take time to generate.' &&
                                 selectedNote.summary !== 'Failed to load summary.' ? 
                                 selectedNote.summary : 'Summary is being processed...'}</span>
                        </>
                      ) : selectedNote.status === 'processing' && 
                         (!selectedNote.summary || selectedNote.summary === 'No summary available') ? (
                        <span className="text-muted">Summary will take time to generate.</span>
                      ) : (
                        <span className="text-muted">Failed to load summary.</span>
                      )}
                    </div>
                  </div>
                </>
              )}
              {selectedNote.view === 'transcript' && !isAnnotationMode && (
                <>
                  {/* Transcript view: scrollable transcript on top, audio docked at bottom (Otter-style) */}
                  <div className="transcript-view-wrapper">
                    <div className="transcript-scroll-area thin-scrollbar" ref={transcriptScrollRef}>{/* ADDED ref */}
                      {selectedNote.loadingTranscript ? (
                        <div className="transcript-loading-container">
                          <div className="transcript-loading-dots">
                            {[0, 1, 2].map((i) => (
                              <div
                                key={i}
                                className="transcript-loading-dot"
                              />
                            ))}
                          </div>
                          <h6 className="transcript-loading-title">
                            Loading Transcript
                          </h6>
                          <p className="transcript-loading-description">
                            Please wait...
                          </p>
                        </div>
                      ) : (audioIsPlaying || audioCurrentTime > 0) && selectedNote.conversation_id ? (
                        /* ── Replay mode: audio is playing → word-by-word sync ── */
                        <ReplayTab
                          conversationId={selectedNote.conversation_id}
                          authIdToken={authIdToken}
                          currentTime={audioCurrentTime}
                          isPlaying={audioIsPlaying}
                          embedded={true}
                          onSeek={(seconds) => audioSeekFnRef.current?.(seconds)}
                        />
                      ) : selectedNote.status === 'processing' ? (
                        selectedNote.transcriptionSegments && selectedNote.transcriptionSegments.length > 0 ? (
                          <div className="transcript-sections">
                            {selectedNote.transcriptionSegments.map((segment, index, arr) => {
                              const isLast = index === arr.length - 1;
                              return (
                              <div key={segment.div_id || index} className="mb-4" ref={isLast ? lastSegmentRef : null}>{/* ADDED ref */}
                                {/* Person icon + timestamp (same style as LiveTranscript) */}
                                <div className="transcript-speaker-header">
                                  <div className="transcript-speaker-avatar">
                                    <BsPersonFill />
                                  </div>
                                  {segment.start_time != null && (
                                    <span className="transcript-paragraph-timestamp">
                                      {Math.floor(segment.start_time / 60)}:{String(Math.floor(segment.start_time % 60)).padStart(2, '0')}
                                    </span>
                                  )}
                                </div>
                                <div className="transcript-segment-text">
                                  {segment?.full_message ? (
                                    <div className="transcript-text segment-text"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(segment.full_message, {
                                          ALLOWED_TAGS: [
                                            'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3',
                                            'ol', 'ul', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'div'
                                          ],
                                          ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'style', 'class']
                                        })
                                      }}
                                    />
                                  ) : (
                                    <div>No transcript available.</div>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center p-4">
                            <p className="text-muted mb-0">No transcript available.</p>
                          </div>
                        )
                      ) : (
                        selectedNote.transcriptionSegments && selectedNote.transcriptionSegments.length > 0 ? (
                          <div className="transcript-sections">
                            {selectedNote.transcriptionSegments.map((segment, index, arr) => {
                              const isLast = index === arr.length - 1;
                              return (
                              <div key={segment.div_id || index} className="mb-4" ref={isLast ? lastSegmentRef : null}>{/* ADDED ref */}
                                {/* Person icon + timestamp (same style as LiveTranscript) */}
                                <div className="transcript-speaker-header">
                                  <div className="transcript-speaker-avatar">
                                    <BsPersonFill />
                                  </div>
                                  {segment.start_time != null && (
                                    <span className="transcript-paragraph-timestamp">
                                      {Math.floor(segment.start_time / 60)}:{String(Math.floor(segment.start_time % 60)).padStart(2, '0')}
                                    </span>
                                  )}
                                </div>
                                <div className="transcript-segment-text">
                                  {segment?.full_message ? (
                                    <div className="transcript-text segment-text"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(segment.full_message, {
                                          ALLOWED_TAGS: [
                                            'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3',
                                            'ol', 'ul', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'div'
                                          ],
                                          ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'style', 'class']
                                        })
                                      }}
                                    />
                                  ) : (
                                    <div>No transcript available.</div>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center p-4">
                            <p className="text-muted mb-0">No transcript available.</p>
                          </div>
                        )
                      )}

                    </div>

                    {/* Audio docked at the bottom of the transcript view */}
                    {(selectedNote.audioUrl || selectedNote.audioPath || selectedNote.s3_path) && selectedNote.status !== 'inprogress' && (
                      <div className="transcript-audio-dock">
                        <AudioPlayer
                          audioUrl={selectedNote.audioUrl || (selectedNote.audioPath ? `${baseUrl}/get_audio?path=${selectedNote.audioPath}` : null)}
                          audioPath={selectedNote.audioPath}
                          s3_path={selectedNote.s3_path}
                          timeInSeconds={
                            getNoteDuration(selectedNote)
                          }
                          onPlayStateChange={(playing) => setAudioIsPlaying(playing)}
                          onTimeUpdate={(time) => setAudioCurrentTime(time)}
                          onSeekRequest={(registerFn) => { audioSeekFnRef.current = registerFn; }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Annotation Mode ── */}
              {isAnnotationMode && (
                <div className="annotation-wrapper">
                  <audio ref={annotationAudioRef} src={selectedNote?.audioUrl} preload="auto" onEnded={() => { setAnnotationPlayingDivId(null); setAnnotationPausedDivId(null); if (annotationPlayIntervalRef.current) { cancelAnimationFrame(annotationPlayIntervalRef.current); annotationPlayIntervalRef.current = null; } }} />
                  {loadingAnnotation ? (
                    <div className="annotation-loading-container">
                      <div className="annotation-loading-dots">
                        {[0, 1, 2].map((i) => (
                          <div key={i} className="annotation-loading-dot" />
                        ))}
                      </div>
                      <h6 className="annotation-loading-title">
                        Loading Annotation
                      </h6>
                      <p className="annotation-loading-description">
                        Please wait...
                      </p>
                    </div>
                  ) : (
                  <>
                  {/* Column Header Row */}
                  <div className="annotation-header-row">
                    <div className="annotation-header-col">
                      <span className="annotation-header-label">Original</span>
                    </div>
                    <div className="annotation-header-col annotation-header-col-right">
                      <span className="annotation-header-label">Annotated</span>
                      <button
                        className="annotation-save-dataset-btn"
                        onClick={handleSaveAllAnnotations}
                        disabled={annotationSavingAll}
                      >
                        {annotationSavingAll ? 'Saving...' : 'Save to the Dataset'}
                      </button>
                    </div>
                  </div>
                  {/* Scrollable segments */}
                  <div className="annotation-scroll-area thin-scrollbar">
                    {annotationSegments.map((segment, index) => {
                      const hasChanges = segment.editedText !== segment.initialEditedText;
                      const status = annotationSaveStatus[segment.div_id];
                      return (
                        <div key={segment.div_id || index} className="annotation-segment">
                          <div className="annotation-row">
                            {/* Original column */}
                            <div className="annotation-col annotation-col-original">
                              <div className="annotation-col-inner">
                                <div className="annotation-col-media">
                                  {selectedNote?.audioUrl && (
                                    <button
                                      className={`annotation-play-btn ${annotationPlayingDivId === segment.div_id ? 'playing' : ''}`}
                                      onClick={() => handleAnnotationPlaySegment(segment, index)}
                                      title="Play this segment"
                                    >
                                      {annotationPlayingDivId === segment.div_id ? <BsPauseFill /> : <BsPlayFill />}
                                    </button>
                                  )}
                                  {segment.start_time != null && (
                                    <span className="annotation-timestamp">
                                      {Math.floor(segment.start_time / 60)}:{String(Math.floor(segment.start_time % 60)).padStart(2, '0')}
                                    </span>
                                  )}
                                </div>
                                <div className="annotation-text-content">{segment.originalText}</div>
                              </div>
                            </div>
                            {/* Annotated column */}
                            <div className="annotation-col annotation-col-editable">
                              <div className="annotated-editor">
                                <div className="annotation-text-content annotation-annotated-text">
                                  {segment.saved && segment.editedText !== segment.originalText ? (
                                    <span
                                      onClick={() => handleAnnotationTextChange(segment.div_id, segment.editedText)}
                                      style={{ cursor: 'pointer' }}
                                      title="Click to edit"
                                    >
                                      {wordDiff(segment.originalText, segment.editedText).map((part, i) => (
                                        <span key={i} style={part.changed ? { color: '#2563eb', fontWeight: 600 } : {}}>
                                          {part.text}
                                        </span>
                                      ))}
                                    </span>
                                  ) : (
                                    <textarea
                                      className={`annotation-textarea ${hasChanges ? 'has-changes' : ''}`}
                                      value={segment.editedText}
                                      onChange={(e) => handleAnnotationTextChange(segment.div_id, e.target.value)}
                                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                      ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                      rows={1}
                                    />
                                  )}
                                </div>
                              </div>
                              {/* Actions directly below the editor */}
                              <div className="annotation-col-actions">
                                {status === 'saved' && <span className="annotation-status annotation-status-saved">✓ Saved</span>}
                                {status === 'error' && <span className="annotation-status annotation-status-error">✗ Error</span>}
                                {hasChanges && !status && (
                                  <span className="annotation-status-group">
                                    <span className="annotation-status annotation-status-modified">Modified</span>
                                    <button
                                      className="annotation-revert-btn"
                                      onClick={(e) => { e.stopPropagation(); handleRevertAnnotation(segment); }}
                                      title="Revert to last saved version"
                                    >
                                      Revert
                                    </button>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Notes Tab */}
              <div
                className="thin-scrollbar"
                style={{ flex: '1 1 auto', minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: '8px' }}
              >
                  {loading ? (
                    <div className="notes-loading-container">
                      <div className="circular-progress-container">
                        <svg 
                          className="circular-progress-svg"
                          viewBox="0 0 50 50"
                        >
                          <circle
                            className="circular-progress-bg"
                            cx="25"
                            cy="25"
                            r="20"
                          />
                          <circle
                            className="circular-progress-fill"
                            cx="25"
                            cy="25"
                            r="20"
                          />
                        </svg>
                      </div>
                      <h6 className="notes-loading-title">
                        Loading Notes
                      </h6>
                      <p className="notes-loading-description">
                        Please wait...
                      </p>
                    </div>
                  ) : (
                    notes.length > 0 || isLiveSession
                      ? renderPaginatedNotes()
                      : (
                        <div className="text-center p-5 bg-light rounded-3 border border-dashed">
                          <div className="mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-journal-text text-primary" viewBox="0 0 16 16">
                              <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
                              <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3z"/>
                            </svg>
                          </div>
                          <h5 className="mb-2">No notes recorded yet</h5>
                          <p className="text-muted">Record your first note to get started with NoteForge</p>
                          <button 
                            className="btn btn-primary mt-3"
                            onClick={() => navigate(routesfe.RECORD)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-mic-fill me-2" viewBox="0 0 16 16">
                              <path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0V3z"/>
                              <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/>
                            </svg>
                            Record Note
                          </button>
                        </div>
                      )
                  )}
                </div>

              {(notes.length > 0 && !loading) && (
                <div className="position-relative d-flex align-items-center mt-3 mb-3 py-2">
                  <div className="d-flex justify-content-center align-items-center w-100">
                    <button
                      className="pagination-btn"
                      disabled={currentPage <= firstPage}
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, firstPage))}
                    >
                      &#10094;
                    </button>

                    <span className="pagination-current">{currentPage}</span>
                    <button
                      className="pagination-btn"
                      disabled={currentPage >= lastPage}
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, lastPage))}
                    >
                      &#10095;
                    </button>
                  </div>
                  
                  {/* Feedback button — absolute so it doesn't shift pagination */}
                  {!selectedNote && (
                    <div className="position-absolute end-0">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleOpenFeedback}
                        title="Share your feedback"
                        style={{
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chat-dots" viewBox="0 0 16 16">
                          <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                          <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a19.492 19.492 0 0 0 .398-2z"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Standalone feedback button when no pagination is shown */}
              {!selectedNote && (!notes.length || loading) && (
                <div className="d-flex justify-content-center align-items-center mt-3 mb-3 py-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleOpenFeedback}
                    title="Share your feedback"
                    style={{
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chat-dots" viewBox="0 0 16 16">
                      <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                      <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a19.492 19.492 0 0 0 .398-2z"/>
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
     <RightSidebar 
       show={rightSidebarVisible} 
       onToggle={toggleRightSidebar}
       onClose={() => setRightSidebarVisible(false)}
     />
    </div>
  );
};

export default MainHome;